import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import Seo from "../components/Seo";

const money = (minor) => `$${(Number(minor || 0) / 100).toFixed(2)}`;

export default function Cart() {
  const {
    lines,
    updateQuantity,
    removeItem,
    clearCart,
    fulfillmentMethod,
    setFulfillmentMethod,
  } = useCart();
  const [products, setProducts] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [checkoutInFlight, setCheckoutInFlight] = useState(false);
  const [error, setError] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [fulfillmentConfig, setFulfillmentConfig] = useState({
    pickup_enabled: false,
    shipping_enabled: false,
  });
  const [shippingAddress, setShippingAddress] = useState({ address_1: "", address_2: "", city: "", state: "", postal_code: "", country: "US", phone: "" });
  const [shippingChoices, setShippingChoices] = useState([]);
  const [selectedShippingQuoteId, setSelectedShippingQuoteId] = useState("");
  const [ratesBusy, setRatesBusy] = useState(false);
  const [ratesError, setRatesError] = useState("");
  const [addressReview, setAddressReview] = useState("");
  const [clock, setClock] = useState(Date.now());
  const [pickupEvents, setPickupEvents] = useState([]);
  const [pickupEventId, setPickupEventId] = useState("");

  const cartPayloadLines = useCallback(() => lines.map(({ product_id, variant_id, quantity, personalization_text, selected_options, personalization, additional_notes }) => ({ product_id, variant_id, quantity, personalization_text, selected_options, personalization, additional_notes })), [lines]);

  const loadQuote = useCallback(async () => {
    if (!lines.length) {
      setQuote(null);
      return;
    }
    setRecalculating(true);
    try {
      const response = await api.post("/cart/quote", {
        lines: cartPayloadLines(),
        fulfillment_method: fulfillmentMethod,
      });
      setQuote(response.data);
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setRecalculating(false);
    }
  }, [lines.length, cartPayloadLines, fulfillmentMethod]);

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/commerce/fulfillment"), api.get("/events")])
      .then(([productsResponse, fulfillmentResponse, eventsResponse]) => {
        setProducts(productsResponse.data || []);
        const config = fulfillmentResponse.data || {};
        setFulfillmentConfig(config);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = (eventsResponse.data || []).filter((event) => {
          const d = new Date(`${event.event_date}T00:00:00`);
          return event.status !== "Canceled" && d >= today;
        });
        setPickupEvents(upcoming);

      })
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading || (!fulfillmentConfig.pickup_enabled && !fulfillmentConfig.shipping_enabled)) return;
    const enabled = fulfillmentMethod === "pickup" ? fulfillmentConfig.pickup_enabled : fulfillmentConfig.shipping_enabled;
    if (!enabled) setFulfillmentMethod(fulfillmentConfig.pickup_enabled ? "pickup" : "shipping");
  }, [loading, fulfillmentConfig, fulfillmentMethod, setFulfillmentMethod]);

  useEffect(() => {
    loadQuote();
    setShippingChoices([]);
    setSelectedShippingQuoteId("");
  }, [loadQuote]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const requestShippingRates = async () => {
    setRatesBusy(true);
    setRatesError("");
    setAddressReview("");
    setShippingChoices([]);
    setSelectedShippingQuoteId("");
    try {
      const response = await api.post("/shipping/rates", {
        lines: cartPayloadLines(),
        address: {
          ...shippingAddress,
          name: customerName.trim(),
          email: customerEmail.trim(),
        },
      });
      const verified = response.data?.validated_address;
      if (verified) {
        setShippingAddress(({ phone }) => ({
          address_1: verified.address_1, address_2: verified.address_2 || "", city: verified.city,
          state: verified.state, postal_code: verified.postal_code, country: verified.country, phone,
        }));
        setAddressReview(`Rates use this verified address: ${[verified.address_1, verified.address_2, verified.city, verified.state, verified.postal_code].filter(Boolean).join(", ")}. Review it before choosing a rate. ${(response.data?.address_warnings || []).join(" ")}`);
      }
      setShippingChoices(response.data?.choices || []);
      if (!response.data?.choices?.length) setRatesError("No live shipping options are available for this address.");
    } catch (err) {
      setRatesError(formatApiError(err));
    } finally {
      setRatesBusy(false);
    }
  };

  const updateShippingAddress = (key, value) => {
    setAddressReview("");
    setShippingAddress((current) => ({ ...current, [key]: value }));
    setShippingChoices([]);
    setSelectedShippingQuoteId("");
    setRatesError("");
  };

  const updateCustomerContact = (setter, value) => {
    setter(value);
    if (fulfillmentMethod === "shipping") {
      setShippingChoices([]);
      setSelectedShippingQuoteId("");
    }
  };

  const productFor = (line) =>
    products.find((product) => product.id === line.product_id);
  const validatedLine = (line) =>
    quote?.lines?.find(
      (item) =>
        item.product_id === line.product_id &&
        item.variant_id === line.variant_id,
    );
  const invalidLine = (line) =>
    quote?.invalid_lines?.find(
      (item) =>
        item.product_id === line.product_id &&
        item.variant_id === line.variant_id,
    );
  const selectedShippingChoice = shippingChoices.find((item) => item.id === selectedShippingQuoteId);
  const selectedShippingExpired = selectedShippingChoice ? new Date(selectedShippingChoice.expires_at).getTime() <= clock : false;

  const handleCheckout = async () => {
    if (!quote?.can_continue || checkoutInFlight) return;
    setCheckoutInFlight(true);
    setError("");

    try {
      const response = await api.post("/cart/checkout", {
        lines: cartPayloadLines(),
        fulfillment_method: fulfillmentMethod,
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim(),
        idempotency_key: `cart:${Date.now()}:${crypto.randomUUID()}`,
        ...(fulfillmentMethod === "shipping" ? { shipping_quote_id: selectedShippingQuoteId, destination: shippingAddress.postal_code } : {}),
        ...(fulfillmentMethod === "pickup" && pickupEventId ? { pickup_event_id: pickupEventId } : {}),
        redirect_url: `${window.location.origin}/quote/checkout?source=cart&type=checkout`,
      });
      const paymentUrl =
        response.data?.paymentUrl ||
        response.data?.url ||
        response.data?.checkout_url;
      if (!paymentUrl) {
        throw new Error("The cart checkout did not return a payment URL.");
      }
      window.location.href = paymentUrl;
    } catch (err) {
      setError(formatApiError(err));
      setCheckoutInFlight(false);
    }
  };

  return (
    <div className="cart-workshop min-h-screen px-6 lg:px-10 py-12">
      <Seo title="Cart" description="Your Beyond Board Woodworking cart." path="/cart" noindex />
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B]">
              Your selections
            </p>
            <h1 className="font-display text-4xl text-[#F5F0E6]">Cart</h1>
          </div>
          <Link to="/gallery">
            <Button variant="outline" className="cart-natural-button">
              Back to Gallery
            </Button>
          </Link>
        </div>
        {error && (
          <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-rose-400/30 bg-rose-950/20 p-4 text-rose-200">
            <span>{error}</span>
            <Button
              type="button"
              variant="outline"
              onClick={loadQuote}
              className="bg-transparent border-rose-300/40 text-rose-100"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Retry
            </Button>
          </div>
        )}
        {loading ? (
          <p className="text-stone-400">Loading cart details...</p>
        ) : !lines.length ? (
          <div className="cart-natural-panel rounded-2xl p-10 text-center">
            <p className="text-stone-300">Your cart is empty.</p>
            <Link to="/gallery">
              <Button className="cart-natural-button mt-5">
                Browse Gallery
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
            <section className="space-y-4">
              {lines.map((line) => {
                const product = productFor(line);
                const validated = validatedLine(line);
                const invalid = invalidLine(line);
                return (
                  <article
                    key={line.line_id}
                    className="cart-natural-panel flex gap-4 rounded-2xl p-4"
                  >
                    <img
                      src={product?.image_url || "/branding/bbw-logo.jpg"}
                      alt=""
                      className="h-24 w-24 rounded-lg object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <h2 className="text-lg text-stone-100">
                        {validated?.name ||
                          product?.name ||
                          "Unavailable product"}
                      </h2>
                      <p className="text-sm text-stone-400">
                        {validated?.variant_name ||
                          product?.variants?.find(
                            (item) => item.id === line.variant_id,
                          )?.name ||
                          ""}
                      </p>
                      {validated?.customization && (
                        <div className="mt-2 space-y-1 text-xs text-stone-400">
                          {Object.values(
                            validated.customization.options || {},
                          ).map((option) => (
                            <p key={option.key}>{option.label}</p>
                          ))}
                          {Object.entries(
                            validated.customization.personalization || {},
                          ).map(([key, value]) => (
                            <p key={key}>
                              {key}: {value}
                            </p>
                          ))}
                          {validated.customization.additional_notes && (
                            <p>
                              Notes: {validated.customization.additional_notes}
                            </p>
                          )}
                        </div>
                      )}
                      {validated && (
                        <div className="mt-2 flex items-baseline gap-2 text-sm">
                          {validated.sale_applied && (
                            <span className="text-stone-500 line-through">
                              {money(validated.regular_price_minor)}
                            </span>
                          )}
                          <span className="text-[#D8A34B]">
                            {money(validated.unit_price_minor)} each
                          </span>
                          {validated.customization_adjustment_minor > 0 && (
                            <span className="text-stone-400">
                              includes customization
                            </span>
                          )}
                        </div>
                      )}
                      {validated?.made_to_order && (
                        <p className="mt-2 text-sm text-[#26C6D5]">
                          We’ll make {validated.made_to_order_quantity} for your
                          order and send it as soon as possible. Add
                          personalization now or reply to your confirmation
                          email.
                        </p>
                      )}
                      {invalid && (
                        <p className="mt-2 text-sm text-rose-300">
                          {invalid.reason}
                        </p>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <div className="cart-quantity-control flex items-center rounded-md">
                          <button
                            type="button"
                            aria-label="Decrease quantity"
                            onClick={() =>
                              line.quantity <= 1
                                ? removeItem(line.line_id)
                                : updateQuantity(
                                    line.line_id,
                                    line.quantity - 1,
                                  )
                            }
                            className="p-2 text-stone-300"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="cart-quantity-value px-2">
                            {line.quantity}
                          </span>
                          <button
                            type="button"
                            aria-label="Increase quantity"
                            onClick={() =>
                              updateQuantity(line.line_id, line.quantity + 1)
                            }
                            className="p-2 text-stone-300"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        {validated && (
                          <span className="text-sm text-stone-300">
                            Line subtotal: {money(validated.line_total_minor)}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeItem(line.line_id)}
                          className="cart-remove-button flex items-center gap-1 text-sm"
                        >
                          <Trash2 className="h-4 w-4" /> Remove
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </section>
            <aside className="cart-natural-panel h-fit rounded-2xl p-5">
              <h2 className="font-display text-2xl text-stone-100">
                Order summary
              </h2>
              <div className="mt-5 space-y-3">
                <label className="block text-sm text-stone-300">
                  Name
                  <input
                    required
                    value={customerName}
                    onChange={(event) => updateCustomerContact(setCustomerName, event.target.value)}
                    placeholder="Your name"
                    className="cart-inset-field mt-1 w-full rounded-md p-2.5"
                  />
                </label>
                {fulfillmentMethod === "shipping" && (
                  <fieldset className="space-y-3 border-t border-stone-100/10 pt-4" data-testid="shipping-address-form">
                    <legend className="text-sm font-medium text-stone-100">Shipping address</legend>
                    <p className="text-xs text-stone-400">Contiguous United States only. Your address stays in this page until checkout.</p>
                    <input required aria-label="Address line 1" autoComplete="address-line1" value={shippingAddress.address_1} onChange={(event) => updateShippingAddress("address_1", event.target.value)} placeholder="Address line 1" className="cart-inset-field w-full rounded-md p-2.5" />
                    <input aria-label="Address line 2" autoComplete="address-line2" value={shippingAddress.address_2} onChange={(event) => updateShippingAddress("address_2", event.target.value)} placeholder="Apartment, suite, etc. (optional)" className="cart-inset-field w-full rounded-md p-2.5" />
                    <div className="grid grid-cols-2 gap-2">
                      <input required aria-label="City" autoComplete="address-level2" value={shippingAddress.city} onChange={(event) => updateShippingAddress("city", event.target.value)} placeholder="City" className="cart-inset-field min-w-0 rounded-md p-2.5" />
                      <input required aria-label="State" autoComplete="address-level1" maxLength={2} value={shippingAddress.state} onChange={(event) => updateShippingAddress("state", event.target.value.toUpperCase())} placeholder="State" className="cart-inset-field min-w-0 rounded-md p-2.5" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input required aria-label="ZIP code" autoComplete="postal-code" inputMode="numeric" value={shippingAddress.postal_code} onChange={(event) => updateShippingAddress("postal_code", event.target.value)} placeholder="ZIP code" className="cart-inset-field min-w-0 rounded-md p-2.5" />
                      <input required aria-label="Phone" autoComplete="tel" value={shippingAddress.phone} onChange={(event) => updateShippingAddress("phone", event.target.value)} placeholder="Phone" className="cart-inset-field min-w-0 rounded-md p-2.5" />
                    </div>
                    <Button type="button" disabled={ratesBusy || !customerName.trim() || !/^\S+@\S+\.\S+$/.test(customerEmail) || !shippingAddress.address_1.trim() || !shippingAddress.city.trim() || shippingAddress.state.length !== 2 || !shippingAddress.postal_code.trim()} onClick={requestShippingRates} className="cart-natural-button w-full" data-testid="request-shipping-rates">
                      {ratesBusy ? "Checking rates..." : "Get live shipping rates"}
                    </Button>
                    {ratesError && <p role="alert" className="text-sm text-rose-300">{ratesError}</p>}
                    {addressReview && <p role="status" className="text-sm text-stone-300">{addressReview}</p>}
                    {shippingChoices.map((choice) => (
                      <label key={choice.id} className={`block cursor-pointer border p-3 ${selectedShippingQuoteId === choice.id ? "border-[#26C6D5] bg-[#26C6D5]/10" : "border-stone-100/15"}`}>
                        <span className="flex items-start gap-2">
                          <input type="radio" name="shipping-rate" checked={selectedShippingQuoteId === choice.id} onChange={() => setSelectedShippingQuoteId(choice.id)} />
                          <span className="min-w-0 flex-1">
                            <span className="flex flex-wrap justify-between gap-x-2 text-sm text-stone-100"><span>{choice.carrier_name} · {choice.service_name}</span><span>{money(choice.amount_minor)}</span></span>
                            {(choice.rate_adjustment_minor > 0 || choice.handling_fee_minor > 0) && <span className="mt-1 block text-xs text-stone-400">Carrier {money(choice.carrier_amount_minor)}{choice.rate_adjustment_minor > 0 ? ` · Rate adjustment ${money(choice.rate_adjustment_minor)}` : ""}{choice.handling_fee_minor > 0 ? ` · Handling ${money(choice.handling_fee_minor)}` : ""}</span>}
                            <span className="block text-xs text-stone-400">{choice.label}{choice.delivery_days ? ` · ${choice.delivery_days} business days after ready to ship` : ""}</span>
                            <span className="block text-[11px] text-stone-500">Rate expires {new Date(choice.expires_at).toLocaleTimeString()}</span>
                          </span>
                        </span>
                      </label>
                    ))}
                    {selectedShippingExpired && <p role="alert" className="text-sm text-rose-300">This rate expired. Request current rates to continue.</p>}
                    {shippingChoices.length > 0 && <p className="text-xs text-stone-400">Made-to-order delivery estimates begin after production is complete.</p>}
                  </fieldset>
                )}
                <label className="block text-sm text-stone-300">
                  Email for order updates
                  <input
                    type="email"
                    required
                    value={customerEmail}
                    onChange={(event) => updateCustomerContact(setCustomerEmail, event.target.value)}
                    placeholder="you@example.com"
                    className="cart-inset-field mt-1 w-full rounded-md p-2.5"
                  />
                </label>
                <label className="block text-sm text-stone-300">
                  Fulfillment
                  <select
                    value={fulfillmentMethod}
                    onChange={(event) =>
                      setFulfillmentMethod(event.target.value)
                    }
                    className="cart-inset-field mt-1 w-full rounded-md p-2.5"
                  >
                    <option
                      value="pickup"
                      disabled={!fulfillmentConfig.pickup_enabled}
                    >
                      Pickup
                    </option>
                    <option
                      value="shipping"
                      disabled={!fulfillmentConfig.shipping_enabled}
                    >
                      Shipping
                    </option>
                  </select>
                </label>
                <p className="text-xs text-stone-500">
                  {(fulfillmentMethod === "pickup" ? fulfillmentConfig.pickup_instructions : fulfillmentConfig.shipping_instructions) || quote?.fulfillment_instructions ||
                    quote?.fulfillment_label ||
                    ""}
                </p>
                {fulfillmentMethod === "pickup" && pickupEvents.length > 0 && (
                  <label className="block text-sm text-stone-300">
                    Pick up at an event (optional)
                    <select
                      value={pickupEventId}
                      onChange={(event) => setPickupEventId(event.target.value)}
                      className="cart-inset-field mt-1 w-full rounded-md p-2.5"
                      data-testid="pickup-event"
                    >
                      <option value="">Shop pickup (no event)</option>
                      {pickupEvents.map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.event_name} — {new Date(`${event.event_date}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · {event.location_name}{event.city ? `, ${event.city}` : ""}{event.state ? `, ${event.state}` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div className="border-t border-stone-100/10 pt-4 space-y-2 text-sm text-stone-300">
                  <div className="flex justify-between">
                    <span>Merchandise</span>
                    <span>{money(quote?.merchandise_subtotal_minor)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>
                      {fulfillmentMethod === "pickup" ? "Pickup" : "Shipping"}
                    </span>
                    <span>{fulfillmentMethod === "shipping" ? money(selectedShippingChoice?.carrier_amount_minor) : money(quote?.shipping_minor)}</span>
                  </div>
                  {fulfillmentMethod === "shipping" && selectedShippingChoice?.handling_fee_minor > 0 && <div className="flex justify-between text-xs text-stone-400"><span>Shipping handling</span><span>{money(selectedShippingChoice.handling_fee_minor)}</span></div>}
                  {fulfillmentMethod === "shipping" && selectedShippingChoice?.rate_adjustment_minor > 0 && <div className="flex justify-between text-xs text-stone-400"><span>Shipping rate adjustment</span><span>{money(selectedShippingChoice.rate_adjustment_minor)}</span></div>}
                  {quote?.free_shipping_remaining_minor > 0 && (
                    <p className="text-xs text-[#26C6D5]">
                      Spend {money(quote.free_shipping_remaining_minor)} more
                      for free shipping.
                    </p>
                  )}
                  <div className="flex justify-between">
                    <span>{quote?.tax_label || "Tax"}</span>
                    <span>{money(fulfillmentMethod === "shipping" ? selectedShippingChoice?.tax_minor : quote?.tax_minor)}</span>
                  </div>
                  {(fulfillmentMethod === "shipping" ? selectedShippingChoice?.processing_fee_minor : quote?.processing_fee_minor) > 0 && (
                    <div className="flex justify-between">
                      <span>
                        {quote.processing_fee_label || "Processing fee"}
                      </span>
                      <span>{money(fulfillmentMethod === "shipping" ? selectedShippingChoice?.processing_fee_minor : quote?.processing_fee_minor)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-stone-100/10 pt-3 text-lg text-stone-100">
                    <span>Total</span>
                    <span>{money(fulfillmentMethod === "shipping" ? selectedShippingChoice?.total_minor : quote?.total_minor)}</span>
                  </div>
                </div>
              </div>
              <Button
                type="button"
                disabled={
                  !quote?.can_continue ||
                  (fulfillmentMethod === "shipping" && (!selectedShippingQuoteId || selectedShippingExpired)) ||
                  !customerName.trim() ||
                  !/^\S+@\S+\.\S+$/.test(customerEmail) ||
                  checkoutInFlight ||
                  recalculating
                }
                onClick={handleCheckout}
                className="cart-natural-button mt-5 w-full opacity-100 disabled:opacity-60"
              >
                {checkoutInFlight
                  ? "Processing payment..."
                  : "Complete payment"}
              </Button>
              {recalculating && (
                <p className="mt-3 text-center text-xs text-stone-500">
                  Recalculating totals...
                </p>
              )}
              {quote?.invalid_lines?.length > 0 && (
                <p className="mt-3 text-sm text-rose-300">
                  Resolve the invalid cart lines before checkout.
                </p>
              )}
              <Button
                type="button"
                variant="ghost"
                onClick={clearCart}
                className="cart-clear-button mt-2 w-full"
              >
                Clear cart
              </Button>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
